import { apiRequest } from '../../../shared/services/apiClient';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import type {
  AiToolPluginCatalogItem,
  AiToolPluginDefinition,
  AiToolPluginSource,
  CreateAiToolPluginRequest,
  UpdateAiToolPluginRequest,
} from '../types/ToolPlugin';

const normalizePlugin = (value: any): AiToolPluginDefinition => ({
  pluginId: String(value?.pluginId ?? ''),
  kernelPluginName: String(value?.kernelPluginName ?? ''),
  displayName: String(value?.displayName ?? ''),
  description: String(value?.description ?? ''),
  isPublished: Boolean(value?.isPublished),
  memberFunctionIds: Array.isArray(value?.memberFunctionIds)
    ? value.memberFunctionIds.map(String)
    : [],
});

const normalizeCatalogItem = (value: any): AiToolPluginCatalogItem => ({
  functionId: String(value?.functionId ?? ''),
  displayName: String(value?.displayName ?? ''),
  kernelFunctionName: String(value?.kernelFunctionName ?? ''),
  source: (String(value?.source ?? 'api') === 'mcp' ? 'mcp' : 'api') as AiToolPluginSource,
  isPublished: Boolean(value?.isPublished),
  memberOfPluginIds: Array.isArray(value?.memberOfPluginIds)
    ? value.memberOfPluginIds.map(String)
    : [],
});

const normalizeUseCaseIds = (value: any): AiPromptUseCaseId[] => (
  Array.isArray(value?.useCaseIds)
    ? value.useCaseIds.map(String) as AiPromptUseCaseId[]
    : []
);

export const listAiToolPlugins = async (): Promise<AiToolPluginDefinition[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/tool-plugins', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizePlugin) : [];
};

export const getAiToolPluginCatalog = async (): Promise<AiToolPluginCatalogItem[]> => {
  const result = await apiRequest<unknown[]>('/api/v1/ai/tool-plugins/tool-catalog', { method: 'GET' });
  return Array.isArray(result) ? result.map(normalizeCatalogItem) : [];
};

export const getAiToolPlugin = async (pluginId: string): Promise<AiToolPluginDefinition> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/tool-plugins/${encodeURIComponent(pluginId)}`,
    { method: 'GET' },
  );
  return normalizePlugin(result);
};

export const getAiToolPluginUseCaseAssignments = async (
  pluginId: string,
): Promise<AiPromptUseCaseId[]> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/tool-plugins/${encodeURIComponent(pluginId)}/use-cases`,
    { method: 'GET' },
  );
  return normalizeUseCaseIds(result);
};

export const createAiToolPlugin = async (
  request: CreateAiToolPluginRequest,
): Promise<AiToolPluginDefinition> => {
  const result = await apiRequest<unknown>('/api/v1/ai/tool-plugins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return normalizePlugin(result);
};

export const updateAiToolPlugin = async (
  pluginId: string,
  request: UpdateAiToolPluginRequest,
): Promise<AiToolPluginDefinition> => {
  const result = await apiRequest<unknown>(
    `/api/v1/ai/tool-plugins/${encodeURIComponent(pluginId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  );
  return normalizePlugin(result);
};

export const deleteAiToolPlugin = async (pluginId: string): Promise<void> => {
  await apiRequest<void>(
    `/api/v1/ai/tool-plugins/${encodeURIComponent(pluginId)}`,
    { method: 'DELETE' },
  );
};
